import { useContext } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import { Box, Tooltip, Typography, alpha } from '@mui/material';
import UserContext from 'src/context/UserContext';
import { SidebarContext } from 'src/contexts/SidebarContext';
import { brand } from 'src/theme/brand';
import { NavItem, navigationFor } from '../../navigation';
import { useNavBadges } from '../../NavBadgesContext';

function MenuLink({
  item,
  count,
  collapsed,
  onNavigate
}: {
  item: NavItem;
  count?: number;
  collapsed: boolean;
  onNavigate: () => void;
}) {
  const Icon = item.icon;
  const link = (
    <Box
      component={RouterLink}
      to={item.to}
      end={item.to === '/'}
      onClick={onNavigate}
      aria-label={collapsed ? item.label : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: 1.25,
        height: 40,
        px: collapsed ? 0 : 1.5,
        borderRadius: '10px',
        color: brand.muted,
        textDecoration: 'none',
        fontSize: 13.5,
        fontWeight: 500,
        lineHeight: 1,
        position: 'relative',
        transition: 'background-color .15s ease, color .15s ease, box-shadow .15s ease',
        '& .nav-icon': { fontSize: 20, flexShrink: 0 },
        '&:hover': { backgroundColor: brand.subtle, color: brand.ink },
        '&.active': {
          backgroundColor: brand.subtle,
          color: brand.ink,
          fontWeight: 600,
          boxShadow: `inset 0 0 0 1px ${brand.border}`,
          '& .nav-icon': { color: brand.ink }
        },
        '&:focus-visible': { outline: `2px solid ${alpha(brand.green, 0.5)}`, outlineOffset: -2 }
      }}
    >
      <Icon className="nav-icon" />
      {!collapsed && (
        <Box
          component="span"
          sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {item.label}
        </Box>
      )}
      {!!count &&
        (collapsed ? (
          <Box
            component="span"
            sx={{ position: 'absolute', top: 8, right: 14, width: 8, height: 8, borderRadius: '50%', backgroundColor: brand.ink }}
          />
        ) : (
          <Box
            component="span"
            aria-label={`${count} waiting`}
            sx={{
              minWidth: 20,
              height: 20,
              px: 0.75,
              borderRadius: '10px',
              backgroundColor: brand.ink,
              color: brand.white,
              fontSize: 11,
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {count > 99 ? '99+' : count}
          </Box>
        ))}
    </Box>
  );

  return collapsed ? (
    <Tooltip title={count ? `${item.label} (${count})` : item.label} placement="right">
      {link}
    </Tooltip>
  ) : (
    link
  );
}

function SidebarMenu({ collapsed = false }: { collapsed?: boolean }) {
  const { closeSidebar } = useContext(SidebarContext);
  const [user] = useContext(UserContext);
  const sections = navigationFor(user?.role);
  const badges = useNavBadges();

  return (
    <Box component="nav" aria-label="Main" sx={{ px: collapsed ? 1.25 : 1.5, pt: 0.5, pb: 2 }}>
      {sections.map((section, index) => (
        <Box key={section.title} sx={{ mt: index === 0 ? 0 : 2 }}>
          {collapsed ? (
            index > 0 && <Box sx={{ mx: 1, mb: 1.5, borderTop: `1px solid ${brand.border}` }} />
          ) : (
            <Typography
              component="div"
              sx={{
                px: 1.5,
                mb: 0.75,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: alpha(brand.ink, 0.38)
              }}
            >
              {section.title}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {section.items.map((item) => (
              <MenuLink
                key={item.to}
                item={item}
                collapsed={collapsed}
                count={item.badge ? badges[item.badge] : undefined}
                onNavigate={closeSidebar}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default SidebarMenu;
